import { Check, Clock, ExternalLink, LoaderCircle, Undo2 } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";

import { useAmountFormatter } from "../../hooks/useAmountFormatter";
import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import { useOtcTiming } from "../../hooks/useOtcTiming";
import type { OtcSellOrder } from "../../types/cryptoSell.types";
import { sellFailureReasonKey } from "../../utils/cryptoSellStatus";
import { chunkAddress } from "../../utils/cryptoSwapAddress";
import { CRYPTO_SELL_HISTORY_PATH, CRYPTO_SELL_PATH } from "../../utils/cryptoSwapLinks";
import { OutcomeShell } from "../order/OrderOutcome";
import { AssetAmount } from "../shared/AssetAmount";
import { DetailRow, MonoValue } from "../shared/CryptoSwapBits";
import { OperationEta } from "../shared/OperationEta";
import { SellTransit } from "../shared/OtcTransit";

const WALLETS_PATH = "/app/wallets";

export interface SaleOutcomeProps {
  sale: OtcSellOrder;
}

/** Where the sale stands once the deposit is no longer awaited: on its way, credited, expired or returned. */
export function SaleOutcome({ sale }: SaleOutcomeProps) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const format = useAmountFormatter();
  const asset = sale.deposit.currency;
  const paid = sale.proceeds.paid_amount ?? sale.proceeds.amount;
  const depositTxUrl = labels.txUrl(sale.deposit.network, sale.deposit.tx_hash);
  const explorer = labels.networkRule(sale.deposit.network)?.explorer ?? "";
  const timing = useOtcTiming();
  const eta = <OperationEta since={sale.deposit_received_at} estimate={timing?.sell[sale.deposit.network]} />;
  const transit = (
    <SellTransit
      className="max-w-lg"
      sendCurrency={asset}
      receiveCurrency={sale.proceeds.currency}
      network={sale.deposit.network}
      payoutKey={sale.payout_key}
    />
  );

  const actions = (
    <div className="flex flex-wrap gap-2">
      <ColocaButton variant="primary" render={<Link to={CRYPTO_SELL_PATH} />}>
        {t("sell_page_new")}
      </ColocaButton>
      <ColocaButton variant="default" render={<Link to={CRYPTO_SELL_HISTORY_PATH} />}>
        {t("order_view_history")}
      </ColocaButton>
    </div>
  );

  const depositLink =
    depositTxUrl && explorer ? (
      <a
        href={depositTxUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-primary hover:underline"
      >
        {t("sell_deposit_tx_link", { explorer })}
        <ExternalLink aria-hidden="true" className="size-3.5" />
      </a>
    ) : null;

  if (sale.status === "COMPLETED") {
    const credited = sale.credit.amount ?? paid;
    return (
      <OutcomeShell icon={Check} tone="success" title={t("sell_outcome_completed_title")}>
        <p className="text-base leading-relaxed text-foreground">
          {t("sell_outcome_completed_message", {
            amount: `${format(credited, sale.credit.currency)} ${sale.credit.currency}`,
          })}
        </p>
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 rounded-2xl bg-muted p-4">
          <DetailRow label={t("sell_summary_proceeds")}>
            <Money value={paid} currency={sale.proceeds.currency} />
          </DetailRow>
          <DetailRow label={t("sell_summary_fee")}>
            <Money value={sale.credit.fee_total ?? "0"} currency={sale.credit.currency} />
          </DetailRow>
          <DetailRow label={t("sell_summary_credited")}>
            <Money value={credited} currency={sale.credit.currency} strong />
          </DetailRow>
        </dl>
        <div className="flex flex-wrap gap-2">
          <ColocaButton variant="primary" render={<Link to={WALLETS_PATH} />}>
            {t("sell_outcome_wallet_link")}
          </ColocaButton>
          <ColocaButton variant="default" render={<Link to={CRYPTO_SELL_PATH} />}>
            {t("sell_page_new")}
          </ColocaButton>
        </div>
      </OutcomeShell>
    );
  }

  if (sale.status === "EXPIRED") {
    return (
      <OutcomeShell icon={Clock} tone="neutral" title={t("sell_outcome_expired_title")}>
        <p className="text-base leading-relaxed text-foreground">
          {t("sell_outcome_expired_message", { asset })}
        </p>
        {actions}
      </OutcomeShell>
    );
  }

  if (sale.status === "REFUNDING" || sale.status === "REFUNDED") {
    const returned = sale.status === "REFUNDED";
    return (
      <OutcomeShell
        icon={Undo2}
        tone="warning"
        title={returned ? t("sell_outcome_refunded_title", { asset }) : t("sell_outcome_refunding_title")}
      >
        <p className="text-base leading-relaxed text-foreground">
          {t(sellFailureReasonKey(sale.failure_reason))}
        </p>
        <RefundWallet sale={sale}>
          {returned ? t("sell_outcome_refunded_to", { asset }) : t("sell_outcome_refunding_to", { asset })}
        </RefundWallet>
        {returned && actions}
      </OutcomeShell>
    );
  }

  if (sale.status === "COP_SENT") {
    return (
      <OutcomeShell icon={LoaderCircle} tone="info" title={t("sell_outcome_cop_sent_title")} spinning>
        {transit}
        <p className="text-base leading-relaxed text-foreground">
          {t("sell_outcome_cop_sent_message", {
            amount: `${format(paid, sale.proceeds.currency)} ${sale.proceeds.currency}`,
            key: sale.payout_key,
          })}
        </p>
        {depositLink}
      </OutcomeShell>
    );
  }

  return (
    <OutcomeShell icon={LoaderCircle} tone="info" title={t("sell_outcome_processing_title")} spinning>
      {transit}
      <p className="text-base leading-relaxed text-foreground">
        {t("sell_outcome_processing_message", { asset })}
      </p>
      {eta}
      {depositLink}
    </OutcomeShell>
  );
}

function RefundWallet({ sale, children }: { sale: OtcSellOrder; children: ReactNode }) {
  const { head, middle, tail } = chunkAddress(sale.refund_address);
  return (
    <div className="flex flex-col gap-1 rounded-2xl bg-muted p-4">
      <span className="text-xs text-muted-foreground">{children}</span>
      <span className="sr-only">{sale.refund_address}</span>
      <span aria-hidden="true" className="flex flex-wrap gap-x-2 font-mono text-sm tabular-nums">
        <span className="font-semibold text-foreground">{head}</span>
        {middle.map((group, index) => (
          <span key={`${group}-${index}`} className="text-muted-foreground">
            {group}
          </span>
        ))}
        <span className="font-semibold text-foreground">{tail}</span>
      </span>
    </div>
  );
}

function Money({ value, currency, strong }: { value: string; currency: string; strong?: boolean }) {
  return (
    <MonoValue className={strong ? "font-semibold" : undefined}>
      <AssetAmount
        value={value}
        currency={currency}
        size="sm"
        className={strong ? "font-semibold" : "font-normal"}
      />{" "}
      {currency}
    </MonoValue>
  );
}
